import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [billingCycle, setBillingCycle] = useState('annual');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const plans = [
    {
      name: 'Стартовый',
      description: 'Для малого бизнеса и стартапов',
      monthlyPrice: 15000,
      annualPrice: 12000,
      users: 'До 10 пользователей',
      features: [
        'Управление лидами и сделками',
        'Базовая воронка продаж',
        'Мобильное приложение',
        'Email поддержка',
        'Базовая аналитика',
        'API доступ'
      ],
      color: 'accent',
      popular: false
    },
    {
      name: 'Профессиональный',
      description: 'Для растущих компаний',
      monthlyPrice: 25000,
      annualPrice: 20000,
      users: 'До 50 пользователей',
      features: [
        'Все из Стартового',
        'ИИ-ассистент Виктория',
        'Кастомные модули (3 шт)',
        'Расширенная аналитика',
        'Приоритетная поддержка 24/7',
        'Интеграции с 1С, Битрикс24',
        'Автоматизация процессов',
        'Обучение команды'
      ],
      color: 'primary',
      popular: true
    },
    {
      name: 'Корпоративный',
      description: 'Для крупных предприятий',
      monthlyPrice: null,
      annualPrice: null,
      users: 'Неограниченно',
      features: [
        'Все из Профессионального',
        'Неограниченные кастомные модули',
        'Выделенный менеджер успеха',
        'SLA 99.9%',
        'Персональная настройка',
        'On-premise развертывание',
        'Интеграция с любыми системами',
        'Индивидуальное обучение'
      ],
      color: 'secondary',
      popular: false
    }
  ];

  const handleDemoClick = () => {
    const demoSection = document.getElementById('demo');
    if (demoSection) {
      const offset = 80;
      const elementPosition = demoSection?.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section id="pricing" ref={sectionRef} className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-card via-background to-card" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl sm:text-5xl font-black text-foreground mb-4">
            Прозрачные цены
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Выберите план, который подходит вашему бизнесу
          </p>

          <div className="inline-flex items-center glassmorphism rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                billingCycle === 'monthly' ?'bg-primary text-black' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Ежемесячно
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                billingCycle === 'annual' ?'bg-primary text-black' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Ежегодно
              <span className="ml-2 text-xs bg-success/20 text-success px-2 py-0.5 rounded-full">
                -20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans?.map((plan, index) => (
            <div
              key={index}
              className={`glassmorphism-strong rounded-2xl p-8 transition-all duration-1000 hover:scale-105 ${
                plan?.popular ? `border-2 border-${plan?.color} neon-glow-${plan?.color}` : ''
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {plan?.popular && (
                <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 px-3 py-1 rounded-full mb-4">
                  <Icon name="Star" size={14} color="var(--color-primary)" />
                  <span className="text-xs font-bold text-primary">Популярный</span>
                </div>
              )}

              <h3 className="text-2xl font-black text-foreground mb-2">{plan?.name}</h3>
              <p className="text-sm text-muted-foreground mb-6">{plan?.description}</p>

              <div className="mb-6">
                {plan?.monthlyPrice ? (
                  <>
                    <div className="flex items-baseline">
                      <span className="text-4xl font-black text-foreground">
                        {(billingCycle === 'annual' ? plan?.annualPrice : plan?.monthlyPrice)?.toLocaleString('ru-RU')}
                      </span>
                      <span className="text-muted-foreground ml-2">₽/мес</span>
                    </div>
                    {billingCycle === 'annual' && (
                      <div className="text-xs text-success mt-1">
                        Экономия {((plan?.monthlyPrice - plan?.annualPrice) * 12)?.toLocaleString('ru-RU')} ₽/год
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-2xl font-bold text-foreground">
                    Индивидуально
                  </div>
                )}
              </div>

              <div className="mb-6">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
                  <span>{plan?.users}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan?.features?.map((feature, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} color={`var(--color-${plan?.color})`} className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant={plan?.popular ? 'default' : 'outline'}
                size="lg"
                fullWidth
                onClick={handleDemoClick}
                iconName="ArrowRight"
                iconPosition="right"
                className={plan?.popular ? 'neon-glow-primary' : ''}
              >
                {plan?.monthlyPrice ? 'Начать работу' : 'Связаться с нами'}
              </Button>
            </div>
          ))}
        </div>

        <div className={`mt-16 text-center transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="glassmorphism rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Нужна помощь с выбором?
            </h3>
            <p className="text-muted-foreground mb-6">
              Наши эксперты помогут подобрать оптимальный план для вашего бизнеса
            </p>
            <Button
              variant="outline"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
            >
              Связаться с экспертом
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;